import type { Contact, ContactRole } from "../types";

export const CONTACT_ROLES: { value: ContactRole; label: string }[] = [
  { value: "agent", label: "Agent" },
  { value: "manager", label: "Manager" },
  { value: "label", label: "Label" },
  { value: "co_promoter", label: "Co-promoter" },
];

export type ContactFormState = {
  name: string;
  role: ContactRole | "";
  agency: string;
  email: string;
  phone: string;
  notes: string;
  artist_ids: string[];
};

export const emptyContactForm = (): ContactFormState => ({
  name: "",
  role: "",
  agency: "",
  email: "",
  phone: "",
  notes: "",
  artist_ids: [],
});

export function contactToForm(contact: Contact): ContactFormState {
  return {
    name: contact.name,
    role: (contact.role as ContactRole) ?? "",
    agency: contact.agency ?? "",
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    notes: contact.notes ?? "",
    artist_ids: contact.artists.map((a) => a.id),
  };
}

function trimOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

export function buildContactPayload(form: ContactFormState): Record<string, unknown> {
  return {
    name: form.name.trim(),
    role: form.role || null,
    agency: trimOrNull(form.agency),
    email: trimOrNull(form.email),
    phone: trimOrNull(form.phone),
    notes: trimOrNull(form.notes),
    artist_ids: form.artist_ids,
  };
}

export function formatContactRole(role?: string | null): string {
  if (!role) return "—";
  const match = CONTACT_ROLES.find((r) => r.value === role);
  return match?.label ?? role.replace(/_/g, " ");
}

export function formatContactSummary(contact: Contact): string {
  const parts: string[] = [];
  if (contact.email) parts.push(contact.email);
  if (contact.phone) parts.push(contact.phone);
  return parts.length ? parts.join(" · ") : "—";
}

export function filterContactsForArtist(contacts: Contact[], artistId?: string | null): Contact[] {
  if (!artistId) return [];
  return contacts.filter((c) => c.artists.some((a) => a.id === artistId));
}

export function sortContactsByRole(contacts: Contact[]): Contact[] {
  const order: Record<string, number> = { agent: 0, manager: 1, label: 2, co_promoter: 3 };
  return [...contacts].sort((a, b) => {
    const aOrder = order[a.role ?? ""] ?? 99;
    const bOrder = order[b.role ?? ""] ?? 99;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.name.localeCompare(b.name);
  });
}
